import styled from '@emotion/styled'
import { Modal, Typography, TextField, IconButton } from '@mui/material'
import React, { FC, useCallback } from 'react'
import { Close } from '@emotion-icons/material-outlined/Close'
import { Search } from '@emotion-icons/material/Search'
import { Colors, Shapes } from 'src/components/theme'
import debounce from 'lodash/debounce'

const ModalContent = styled.div`
  background-color: ${Colors.bgWhite};
  border-radius: ${Shapes.borderRadius}px;
  overflow: hidden;
`

const Header = styled.div`
  padding: ${Shapes.spacing[4]}px;
  position: relative;
`

const CloseWrapper = styled.div`
  position: absolute;
  top: ${Shapes.spacing[4]}px;
  right: ${Shapes.spacing[4]}px;
  display: flex;
  justify-content: flex-end;
`

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const Subtitle = styled(Typography)`
  margin-bottom: 14px;
`
const Content = styled.div`
  margin-top: ${Shapes.spacing[4]}px;
  padding: ${Shapes.spacing[4]}px;
  overflow-y: auto;
`

const SearchField = styled(TextField)`
  position: absolute;
  bottom: -20px;
  width: calc(100% - ${2 * Shapes.spacing[4]}px);

  & fieldset {
    border: 0;
    box-shadow: 0px 1px 2px ${Colors.shadowInputHover};
  }
`

type Variant = 'default' | 'primary'

type SearchableModalProps = {
  open: boolean
  header?: React.ReactNode
  title?: string
  titleIcon?: React.ReactNode
  subtitle?: string
  onClose?: () => void
  onFilterChange?: (filter: string) => void
  children?: React.ReactNode
  placeholder?: string
  width?: number
  maxHeight?: number
  minHeight?: number
  variant?: Variant
  hideSearch?: boolean
}

const VARIANT_COLORS: Record<Variant, any> = {
  default: {
    headerBg: '#e8eaf2',
    text: Colors.textDark,
    closeIcon: Colors.blueDark,
    contentBg: 'white',
  },
  primary: {
    headerBg: Colors.blueDark,
    text: 'white',
    closeIcon: 'white',
    contentBg: '#fbfbff',
  },
}

export const SearchableModal: FC<SearchableModalProps> = ({
  open,
  onClose,
  children,
  header,
  title,
  titleIcon,
  subtitle,
  onFilterChange,
  placeholder = 'Search...',
  width = 800,
  maxHeight = 600,
  minHeight = 200,
  variant = 'default',
  hideSearch = false,
}) => {
  const handleFilterChange = debounce(
    useCallback(
      (evt: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => onFilterChange?.(evt.target.value),
      [onFilterChange]
    ),
    300
  )

  const colors = VARIANT_COLORS[variant]

  return (
    <Modal open={open} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }} onClose={onClose}>
      <ModalContent style={{ minWidth: width }}>
        <Header style={{ backgroundColor: colors.headerBg, color: colors.text }}>
          <CloseWrapper>
            <IconButton size="small" onClick={onClose}>
              <Close size={20} color={colors.closeIcon} />
            </IconButton>
          </CloseWrapper>
          {header ? (
            header
          ) : (
            <>
              <TitleWrapper>
                {titleIcon}
                <Typography fontSize={18}>{title}</Typography>
              </TitleWrapper>
              <Subtitle fontSize={13}>{subtitle}</Subtitle>
            </>
          )}
          {!hideSearch && (
            <SearchField
              onChange={handleFilterChange}
              variant="outlined"
              fullWidth
              placeholder={placeholder}
              InputProps={{
                startAdornment: <Search size={24} color={Colors.blueDark} />,
                style: { height: 40, backgroundColor: 'white', border: 0, fontSize: 14, gap: 8 },
              }}
            />
          )}
        </Header>
        <Content style={{ minHeight, maxHeight, backgroundColor: colors.contentBg }}>{children}</Content>
      </ModalContent>
    </Modal>
  )
}
